import { create } from 'zustand';
import type { UserProfile } from '@beast-keeper/shared';
import { api } from '../services/api';

const DEVICE_ID_KEY = 'beastkeeper_did';
const TOKEN_KEY = 'beastkeeper_token';

function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = 'did_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

interface UserState {
  deviceId: string;
  nickname: string | null;
  token: string | null;
  bestScore: number;
  bestTitle: string | null;
  unlockedTitles: string[];
  isHydrated: boolean;

  register: (nickname: string) => Promise<void>;
  fetchProfile: () => Promise<void>;
  updateBestScore: (score: number, title: string) => void;
  isLoggedIn: () => boolean;
}

export const useUserStore = create<UserState>((set, get) => ({
  deviceId: getDeviceId(),
  nickname: null,
  token: localStorage.getItem(TOKEN_KEY),
  bestScore: 0,
  bestTitle: null,
  unlockedTitles: [],
  isHydrated: false,

  register: async (nickname: string) => {
    const { deviceId } = get();
    let res;
    try {
      res = await api.post('/users/register', { deviceId, nickname });
    } catch (e: any) {
      console.error('[register] 注册失败', e);
      if (e?.response) {
        const msg = e.response.data?.message || e.response.statusText;
        throw new Error(`服务器错误 (${e.response.status}): ${msg}`);
      }
      if (e?.request) {
        throw new Error('无法连接服务器，请检查网络');
      }
      throw new Error(e?.message || '注册请求失败');
    }
    const { user, token } = res.data;
    localStorage.setItem(TOKEN_KEY, token);
    set({
      nickname: user.nickname,
      token,
      bestScore: user.bestScore,
      bestTitle: user.bestTitle,
      unlockedTitles: user.unlockedTitles || [],
      isHydrated: true,
    });
  },

  fetchProfile: async () => {
    const { token } = get();
    if (!token) return;
    try {
      const res = await api.get('/users/me');
      const user = res.data.user as UserProfile;
      set({
        nickname: user.nickname,
        bestScore: user.bestScore,
        bestTitle: user.bestTitle,
        unlockedTitles: user.unlockedTitles || [],
        isHydrated: true,
      });
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      set({ token: null, isHydrated: true });
    }
  },

  updateBestScore: (score: number, title: string) => {
    const { bestScore } = get();
    if (score > bestScore) {
      set({ bestScore: score, bestTitle: title });
      const titles = get().unlockedTitles;
      if (!titles.includes(title)) {
        set({ unlockedTitles: [...titles, title] });
      }
    }
  },

  isLoggedIn: () => !!get().token,
}));
